import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import Container from "@/components/ui/container";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import cacheKeys from "@/config/cache-keys";
import AppService from "@/services/AppService";
import type { APIResponse } from "@/types/APIresponse";
import type { AddGValuePayload, GValue } from "@/types/gValue/gValue";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

const service = new AppService();

const GValueQueryPage = () => {
	const { t } = useTranslation();
	const [value, setValue] = useState("");
	const [date, setDate] = useState("");
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const queryClient = useQueryClient();

	const { data, isLoading, isError, error, refetch } = useQuery<
		APIResponse<GValue[]>
	>({
		queryKey: cacheKeys.GVALUE,
		queryFn: service.getValues,
	});

	const mutation = useMutation<
		GValue,
		AxiosError<APIResponse<null>>,
		AddGValuePayload
	>({
		mutationFn: (payload: AddGValuePayload) => service.addValue(payload),
		onSuccess: () => {
			setValue("");
			setDate("");
			refetch();
		},
		onError: (error) => {
			toast.error(
				`Feil ved legging til G-verdi: ${
					error.response?.data.LocalizedMessage || "Ukjent feil"
				}`
			);
		},
	});

	const deleteMutation = useMutation<APIResponse<null>, Error, string>({
		mutationFn: (id: string) => service.deleteValue(id),
		onMutate: (id) => {
			setDeletingId(id);
		},
		onSuccess: () => {
			toast.success("G-verdi slettet vellykket!");
			queryClient.invalidateQueries({ queryKey: cacheKeys.GVALUE });
			setDeletingId(null);
		},
		onError: (error) => {
			toast.error(`Sletting feilet: ${error.message || "Ukjent feil"}`);
			setDeletingId(null);
		},
	});

	const formatNumberForDisplay = (numberString: string): string => {
		if (!numberString) {
			return "";
		}
		const digits = numberString.replace(/[^0-9]/g, "");
		return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
	};

	const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const rawValue = e.target.value.replace(/[^0-9]/g, "");
		setValue(rawValue);
	};

	const handleSubmit = () => {
		const newVal = Number(value);
		if (!value || isNaN(newVal)) {
			toast.error("Ugyldig verdi: Vennligst skriv inn et gyldig tall.");
			return;
		}
		if (!date) {
			toast.error("Mangler dato: Vennligst velg en utløpsdato.");
			return;
		}

		const selectedDate = new Date(date);
		const now = new Date();
		if (selectedDate < now) {
			toast.warning("Advarsel: Utløpsdatoen er i fortiden.");
		}

		const payload: AddGValuePayload = {
			value: newVal,
			expires_at: selectedDate.toISOString(),
		};

		mutation.mutate(payload);
	};

	if (isLoading) return <div>{t("admin.loading.loading-gvalue")}</div>;
	if (isError)
		return (
			<div>
				{t("admin.create.form.error.error-loading.g-error")} {error?.message}
			</div>
		);

	const gValues = data?.Result || [];
	const sortedGValues = [...gValues].sort((a, b) => {
		const aExpired = new Date(a.expires_at) < new Date();
		const bExpired = new Date(b.expires_at) < new Date();
		return aExpired === bExpired ? 0 : aExpired ? 1 : -1;
	});

	return (
		<>
			<Container className="mx-auto py-6 bg-soft-white dark:bg-black">
				<h1 className="text-2xl font-bold mb-4">{t("admin.gvalue.gvalue")} </h1>
				<div className="mb-6 space-y-4">
					<div>
						<Label>{t("admin.gvalue.gvalue-new")}</Label>
						<Input
							value={formatNumberForDisplay(value)}
							type="text"
							onChange={handleValueChange}
							placeholder={t("admin.gvalue.gvalue-placeholder")}
						/>
					</div>
					<div>
						<Label>{t("admin.gvalue.date")}</Label>
						<Input
							type="date"
							onChange={(e) => setDate(e.target.value)}
							value={date}
						/>
					</div>
					<Button onClick={handleSubmit} size="lg">
						{t("admin.button.save")}
					</Button>
				</div>
				<Table className="mt-6">
					<TableCaption>{t("admin.gvalue.caption")}</TableCaption>
					<TableHeader>
						<TableRow>
							<TableHead>{t("admin.gvalue.value")}</TableHead>
							<TableHead>{t("admin.gvalue.date")} At</TableHead>
							<TableHead>{t("admin.gvalue.status")}</TableHead>
							<TableHead>{t("admin.table.actions")}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{sortedGValues.map((entry) => {
							const isExpired = new Date(entry.expires_at) < new Date();
							return (
								<TableRow
									key={entry.id}
									className={isExpired ? "bg-red-100" : ""}
								>
									<TableCell>{entry.value}</TableCell>
									<TableCell>
										{entry.expires_at
											? new Date(entry.expires_at).toLocaleDateString()
											: "N/A"}
									</TableCell>
									<TableCell>
										{isExpired ? (
											<span className="text-red-500">
												{t("admin.gvalue.inactive")}
											</span>
										) : (
											<span className="text-green-500">
												{t("admin.gvalue.active")}
											</span>
										)}
									</TableCell>
									<TableCell>
										<Button
											variant="destructive"
											className="dark:text-soft-white"
											onClick={() => {
												if (window.confirm(`Slette G-verdi ${entry.value}?`)) {
													deleteMutation.mutate(entry.id);
												}
											}}
											disabled={deletingId === entry.id}
										>
											{deletingId === entry.id
												? t("admin.button.deleting")
												: t("admin.button.delete")}
										</Button>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</Container>
		</>
	);
};

export default GValueQueryPage;
